import {policy,policyText,type PolicyFragment} from './governedPromptAssembly.ts';
export const DEFAULT_LEADERSHIP_LENS_ID = "coach_veera_default" as const;

export const LEADERSHIP_FRAMEWORK_PACK_IDS = [
  "marshall_goldsmith_stakeholder_centered",
  "grow",
  "immunity_to_change",
  "solution_focused",
  "co_active",
  "authentic_leadership",
  "radical_candor",
  "adaptive_leadership",
  "executive_presence",
  "inclusive_leadership",
  "servant_leadership",
] as const;

export const LEGACY_LEADERSHIP_LENS_IDS = [
  "stakeholder_centered_leadership",
  "situational_leadership",
  "communication_influence",
] as const;

export const LEADERSHIP_LENS_IDS = [
  DEFAULT_LEADERSHIP_LENS_ID,
  ...LEADERSHIP_FRAMEWORK_PACK_IDS,
  ...LEGACY_LEADERSHIP_LENS_IDS,
] as const;

export type LeadershipLensId = (typeof LEADERSHIP_LENS_IDS)[number];

export interface LeadershipLensOption {
  id: LeadershipLensId;
  label: string;
  description: string;
  promptFocus: string;
}

export const LEADERSHIP_LENS_OPTIONS: LeadershipLensOption[] = [
  {
    id: "coach_veera_default",
    label: "Coach Veera Default",
    description: "A balanced lens for noticing real leadership moments, commitments, and follow-through.",
    promptFocus: "balance observable leadership moments, commitments, and follow-through",
  },
  {
    id: "marshall_goldsmith_stakeholder_centered",
    label: "Stakeholder Centered Coaching",
    description: "Focus on what others experience, what feedback reveals, and what commitments become visible.",
    promptFocus: "emphasize stakeholder experience, observable feedback, and visible commitments",
  },
  {
    id: "grow",
    label: "GROW",
    description: "Focus on goals, current reality, options, and the committed way forward.",
    promptFocus: "emphasize clear goals, honest current reality, options, and committed next steps",
  },
  {
    id: "immunity_to_change",
    label: "Immunity to Change",
    description: "Focus on repeated stuckness, competing commitments, and safe assumption-testing.",
    promptFocus: "emphasize repeated stuckness, competing commitments, big assumptions, and safe tests",
  },
  {
    id: "solution_focused",
    label: "Solution-Focused Coaching",
    description: "Focus on useful exceptions, existing resources, desired future, and the next small shift.",
    promptFocus: "emphasize useful exceptions, existing resources, desired future, and small next moves",
  },
  {
    id: "co_active",
    label: "Co-Active Coaching",
    description: "Focus on values, presence, whole-person tension, fulfillment, balance, and choice.",
    promptFocus: "emphasize values, presence, whole-person tension, fulfillment, balance, and choice",
  },
  {
    id: "authentic_leadership",
    label: "Authentic Leadership",
    description: "Focus on values, consistency, self-awareness, and how leadership shows up in real moments.",
    promptFocus: "emphasize values, consistency, self-awareness, and observable leadership moments",
  },
  {
    id: "radical_candor",
    label: "Radical Candor",
    description: "Focus on care, direct challenge, feedback, clarity, and relational trust.",
    promptFocus: "emphasize care, direct challenge, feedback clarity, and relational trust",
  },
  {
    id: "adaptive_leadership",
    label: "Adaptive Leadership",
    description: "Focus on technical versus adaptive work, values tension, uncertainty, and shared responsibility.",
    promptFocus: "emphasize technical versus adaptive work, values tension, uncertainty, and shared responsibility",
  },
  {
    id: "executive_presence",
    label: "Executive Presence",
    description: "Focus on signal clarity, composure, pacing, decision framing, and influence.",
    promptFocus: "emphasize signal clarity, composure, pacing, decision framing, and influence",
  },
  {
    id: "inclusive_leadership",
    label: "Inclusive Leadership",
    description: "Focus on voice, belonging, fairness, perspective-taking, and shared decision quality.",
    promptFocus: "emphasize voice, belonging, fairness, perspective-taking, and shared decision quality",
  },
  {
    id: "servant_leadership",
    label: "Servant Leadership",
    description: "Focus on listening, stewardship, empowerment, ethical service, and others' growth.",
    promptFocus: "emphasize listening, stewardship, empowerment, ethical service, and others' growth",
  },
];

export function isLeadershipLensId(value: string | null | undefined): value is LeadershipLensId {
  return LEADERSHIP_LENS_IDS.includes(value as LeadershipLensId);
}

export function isCanonicalLeadershipLensId(value: string | null | undefined): value is Exclude<LeadershipLensId, (typeof LEGACY_LEADERSHIP_LENS_IDS)[number]> {
  return value === DEFAULT_LEADERSHIP_LENS_ID || LEADERSHIP_FRAMEWORK_PACK_IDS.includes(value as (typeof LEADERSHIP_FRAMEWORK_PACK_IDS)[number]);
}

export function resolveLeadershipLens(value: string | null | undefined): LeadershipLensOption {
  const id = isCanonicalLeadershipLensId(value) ? value : DEFAULT_LEADERSHIP_LENS_ID;
  return LEADERSHIP_LENS_OPTIONS.find((option) => option.id === id) ?? LEADERSHIP_LENS_OPTIONS[0];
}

export const LENS_TO_PACK_ID: Partial<Record<LeadershipLensId, string>> = {
  "marshall_goldsmith_stakeholder_centered": "marshall_goldsmith_stakeholder_centered",
  "grow": "grow",
  "immunity_to_change": "immunity_to_change",
  "solution_focused": "solution_focused",
  "co_active": "co_active",
  "authentic_leadership": "authentic_leadership",
  "radical_candor": "radical_candor",
  "adaptive_leadership": "adaptive_leadership",
  "executive_presence": "executive_presence",
  "inclusive_leadership": "inclusive_leadership",
  "servant_leadership": "servant_leadership",
  // "coach_veera_default" intentionally omitted. It uses default behavior.
};

export function resolveLeadershipLensPackId(lensId: string | null | undefined): string | null {
  if (!isCanonicalLeadershipLensId(lensId)) return null;
  return LENS_TO_PACK_ID[lensId] ?? null;
}

const LENS_GUARDRAILS=policy`This lens is a user-selected framing preference, not an assessment, label, or instruction to judge the leader. Do not compare leaders, assign numbers, name defects, infer personality, judge work quality, or produce prescriptive development plans from the lens.`;
/** Source-defined instructions stay policy; resolved canonical descriptors stay data. */
export function buildLeadershipLensPromptPolicy(value:string|null|undefined):PolicyFragment{
  const lens=resolveLeadershipLens(value);
  return policy`Leadership lens framing preference: ${lens.label}.
Use this lens to shape reflection questions and Leadership Record framing by helping the leader notice these canonical focus descriptors: ${lens.promptFocus}.
${LENS_GUARDRAILS}`;
}
export function buildLeadershipLensPromptGuidance(value: string | null | undefined): string {
  const lens = resolveLeadershipLens(value);
  return [
    `Leadership lens framing preference: ${lens.label}.`,
    `Use this lens to shape reflection questions and Leadership Record framing by helping the leader notice ${lens.promptFocus}.`,
    policyText(LENS_GUARDRAILS),
  ].join(" ");
}
